import React, { useState, useCallback, useEffect } from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import HistoryEduOutlined from '@mui/icons-material/HistoryEduOutlined';
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ReactDOMServer from 'react-dom/server';
import n2words from 'n2words';
import CollaborateurSelect from './common/CollaborateurSelect';
import AttestationDomiciliationSalaire from '../templates/AttestationDomiciliationSalaire';
import AttestationSalaire from '../templates/AttestationSalaire';
import AttestationTravail from '../templates/AttestationTravail';
import ContratCDI   from "../templates/ ContratCDI";
import ContratCDD   from "../templates/ContratCDD";
import Modal from '../../../components/Modal/Modal';
import { AppButton } from '../../../components/UI';

const templates = [
    { id: 'attestation_domiciliation_salaire', name: 'Attestation de Domiciliation de Salaire', short: 'Domiciliation de salaire', icon: AccountBalanceOutlined, component: AttestationDomiciliationSalaire },
    { id: 'attestation_salaire', name: 'Attestation de Salaire', short: 'Attestation de salaire', icon: PaymentsOutlined, component: AttestationSalaire },
    { id: 'attestation_travail', name: 'Attestation de Travail', short: 'Attestation de travail', icon: WorkOutlineOutlined, component: AttestationTravail },
    { id: 'contrat_cdi', name: 'Contrat CDI', short: 'Contrat CDI', icon: HistoryEduOutlined, component: ContratCDI },
    { id: 'contrat_cdd', name: 'Contrat CDD', short: 'Contrat CDD', icon: EditNoteOutlined, component: ContratCDD },
];

// Fields shown for each template (the "date" field is common to all of them).
const FIELDS = {
    banque: { label: 'Banque', placeholder: 'Nom de la banque' },
    agence: { label: 'Agence', placeholder: "Nom de l'agence" },
    numeroCompte: { label: 'Numéro de compte', placeholder: 'Numéro de compte bancaire', full: true },
    cin: { label: 'CIN', placeholder: 'Numéro CIN' },
    cnss: { label: 'CNSS', placeholder: 'Numéro CNSS' },
    poste: { label: 'Poste', placeholder: 'Poste occupé' },
    salaire: { label: 'Salaire', placeholder: 'Ex : 10000', type: 'number', suffix: 'DH' },
    adresse: { label: 'Adresse', placeholder: 'Adresse', full: true },
    situationFamille: { label: 'Situation familiale', placeholder: 'Ex : célibataire, marié(e)…' },
    dateNaissance: { label: 'Date de naissance', type: 'date' },
    position: { label: 'Position', placeholder: 'Stagiaire, employé…' },
};
const TEMPLATE_FIELDS = {
    attestation_domiciliation_salaire: ['banque', 'agence', 'numeroCompte'],
    attestation_salaire: ['cin', 'cnss', 'poste', 'salaire'],
    attestation_travail: ['cin', 'cnss', 'poste'],
    contrat_cdi: ['adresse', 'situationFamille', 'cin', 'cnss', 'poste', 'salaire', 'dateNaissance'],
    contrat_cdd: ['adresse', 'cin', 'poste', 'salaire', 'position'],
};

const todayIso = () => new Date().toISOString().substring(0, 10);
const emptyValues = () => ({
    banque: '', agence: '', numeroCompte: '', cin: '', cnss: '', poste: '', salaire: '',
    adresse: '', situationFamille: '', dateNaissance: todayIso(), position: '', date: todayIso(),
});

const SectionLabel = ({ step, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: '#03407b', color: '#fff', fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{step}</Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{children}</Typography>
    </Box>
);

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } };

const GenerateModal = ({ open, onClose }) => {
    const [selectedCollaborateur, setSelectedCollaborateur] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState('attestation_domiciliation_salaire');
    const [values, setValues] = useState(emptyValues);

    const setField = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

    useEffect(() => {
        if (selectedCollaborateur?.fonction) setField('poste', selectedCollaborateur.fonction);
    }, [selectedCollaborateur]);

    const selectedTpl = templates.find((t) => t.id === selectedTemplate);
    const TemplateComponent = selectedTpl?.component;

    const formatDateFr = (isoDateStr) => {
        if (!isoDateStr) return '';
        const [year, month, day] = isoDateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    const salaireLettre = () => (values.salaire
        ? n2words(parseInt(values.salaire, 10), { lang: 'fr' }).replace(/^\w/, c => c.toUpperCase())
        : '………');

    const generateHTML = () => {
        if (!selectedCollaborateur || !TemplateComponent) return '';
        const { banque, agence, numeroCompte, cin, cnss, poste, salaire, adresse, situationFamille, dateNaissance, position, date } = values;
        const commonProps = { nomComplet: selectedCollaborateur.label, date: formatDateFr(date) };
        const dateDebut = formatDateFr(selectedCollaborateur.dateIntegration);

        switch (selectedTemplate) {
            case 'attestation_domiciliation_salaire':
                return ReactDOMServer.renderToStaticMarkup(
                    <TemplateComponent {...commonProps} banque={banque} agence={agence} numeroCompte={numeroCompte} />
                );
            case 'attestation_salaire':
                return ReactDOMServer.renderToStaticMarkup(
                    <TemplateComponent {...commonProps} cin={cin} cnss={cnss} poste={poste} salaire={salaire} salaireLettre={salaireLettre()} />
                );
            case 'attestation_travail':
                return ReactDOMServer.renderToStaticMarkup(
                    <TemplateComponent {...commonProps} cin={cin} cnss={cnss} poste={poste} dateDebut={dateDebut} />
                );
            case 'contrat_cdi':
                return ReactDOMServer.renderToStaticMarkup(
                    <TemplateComponent {...commonProps} cin={cin} cnss={cnss} poste={poste} dateDebut={dateDebut}
                        salaire={salaire} salaireLettre={salaireLettre()} titre={selectedCollaborateur.titre}
                        tel={selectedCollaborateur.tel} situationFamille={situationFamille} adresse={adresse} dateNaissance={dateNaissance} />
                );
            case 'contrat_cdd':
                return ReactDOMServer.renderToStaticMarkup(
                    <TemplateComponent {...commonProps} cin={cin} poste={poste} dateDebut={dateDebut}
                        salaire={salaire} adresse={adresse} position={position} />
                );
            default:
                return '';
        }
    };

    const resetForm = useCallback(() => {
        setSelectedCollaborateur(null);
        setSelectedTemplate('attestation_domiciliation_salaire');
        setValues(emptyValues());
    }, []);

    const handleCloseAndClear = useCallback(() => {
        resetForm();
        onClose();
    }, [resetForm, onClose]);

    const handlePrint = () => {
        const win = window.open('', '_blank');
        const htmlContent = generateHTML();

        const printStyles = `
            <style>
              @page { size: A4; margin: 20mm; }
              @media print {
                @page { margin: 20mm; }
                body { margin: 0 !important; padding: 0 !important; }
                .page-break { page-break-after: always; }
                * { box-sizing: border-box; }
              }
              body {
                margin: 0;
                padding: 0;
                font-family: 'Arial', sans-serif;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                background: white;
                overflow: hidden;
              }
              html {
                margin: 0;
                padding: 0;
                height: auto;
              }
              .document-container {
                width: 100%;
                max-width: 210mm;
                margin: 0 auto;
                padding: 0;
                box-sizing: border-box;
              }
            </style>
          `;

        win.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="UTF-8">
                <title></title>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                ${printStyles}
              </head>
              <body>
                <div class="document-container">
                  ${htmlContent}
                </div>
              </body>
            </html>
          `);

        win.document.close();

        win.addEventListener('load', () => {
            setTimeout(() => {
                win.focus();
                win.print();
            }, 1000);

            win.addEventListener('afterprint', () => {
                win.close();
            });

            win.addEventListener('focus', () => {
                setTimeout(() => {
                    win.close();
                }, 150000);
            });
        });
    };

    const ready = !!(selectedCollaborateur && TemplateComponent);
    const fields = TEMPLATE_FIELDS[selectedTemplate] || [];

    const form = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
                <SectionLabel step={1}>Collaborateur</SectionLabel>
                <CollaborateurSelect
                    id="collaborateur-select"
                    value={selectedCollaborateur}
                    onChange={(collab) => {
                        const fullData = collab?.data || {};
                        setSelectedCollaborateur(collab ? {
                            ...collab,
                            ...fullData,
                            fonction: fullData.fonction || fullData.fonction_label || '',
                            dateIntegration: fullData.dateIntegration || '',
                            titre: fullData.titre || '',
                            tel: fullData.telephone || '',
                        } : null);
                    }}
                />
            </Box>

            <Box sx={{ opacity: selectedCollaborateur ? 1 : 0.5, pointerEvents: selectedCollaborateur ? 'auto' : 'none', transition: 'opacity 0.2s' }}>
                <SectionLabel step={2}>Modèle de document</SectionLabel>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1 }}>
                    {templates.map((tpl) => {
                        const Icon = tpl.icon;
                        const selected = selectedTemplate === tpl.id;
                        return (
                            <Box
                                key={tpl.id}
                                role="radio"
                                aria-checked={selected}
                                tabIndex={0}
                                onClick={() => setSelectedTemplate(tpl.id)}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedTemplate(tpl.id)}
                                sx={{
                                    position: 'relative', display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '10px', cursor: 'pointer',
                                    border: `1.5px solid ${selected ? '#03407b' : '#e2e8f0'}`,
                                    background: selected ? '#f0f7fd' : '#fff',
                                    transition: 'all 0.15s ease',
                                    '&:hover': { borderColor: '#aed6f1' },
                                    '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
                                }}
                            >
                                <Icon sx={{ fontSize: 20, color: selected ? '#03407b' : '#94a3b8' }} />
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: selected ? 700 : 500, color: selected ? '#03407b' : '#334155', lineHeight: 1.25 }}>
                                    {tpl.short}
                                </Typography>
                                {selected && <CheckCircle sx={{ position: 'absolute', top: 4, right: 4, fontSize: 14, color: '#03407b' }} />}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {selectedCollaborateur && (
                <Box>
                    <SectionLabel step={3}>Informations du document</SectionLabel>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                        <TextField size="small" type="date" label="Date du document" value={values.date}
                            onChange={(e) => setField('date', e.target.value)} sx={inputSx} slotProps={{ inputLabel: { shrink: true } }} />
                        {fields.map((name) => {
                            const f = FIELDS[name];
                            return (
                                <TextField
                                    key={name}
                                    size="small"
                                    type={f.type || 'text'}
                                    label={f.label}
                                    placeholder={f.placeholder}
                                    value={values[name]}
                                    onChange={(e) => setField(name, e.target.value)}
                                    sx={{ ...inputSx, gridColumn: f.full ? { sm: '1 / -1' } : undefined }} slotProps={{ inputLabel: { shrink: true }, input: f.suffix ? { endAdornment: <InputAdornment position="end">{f.suffix}</InputAdornment> } : undefined }}
                                />
                            );
                        })}
                    </Box>
                    {['attestation_salaire', 'contrat_cdi'].includes(selectedTemplate) && values.salaire && (
                        <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 1 }}>
                            En lettres : <b>{salaireLettre()} dirhams</b>
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );

    const preview = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 420 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>
                Aperçu {selectedTpl && <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>— {selectedTpl.name}</Box>}
            </Typography>
            {ready ? (
                <Box component="iframe" title="Aperçu du document" srcDoc={generateHTML()}
                    sx={{ flex: 1, width: '100%', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', boxShadow: '0 4px 16px rgba(15,23,42,0.06)' }} />
            ) : (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, borderRadius: '10px', border: '1.5px dashed #cbd5e1', background: '#f8fafc', color: '#94a3b8', p: 3, textAlign: 'center' }}>
                    <DescriptionOutlined sx={{ fontSize: 40 }} />
                    <Typography sx={{ fontSize: '0.88rem' }}>Choisissez un collaborateur pour voir l'aperçu du document.</Typography>
                </Box>
            )}
        </Box>
    );

    return (
        <Modal
            open={open}
            onClose={handleCloseAndClear}
            title="Générer un document"
            maxWidth="lg"
            actions={(
                <>
                    {selectedCollaborateur && selectedTpl && (
                        <Typography sx={{ mr: 'auto', fontSize: '0.82rem', color: '#64748b' }}>
                            <b style={{ color: '#03407b' }}>{selectedTpl.name}</b> pour {selectedCollaborateur.label}
                        </Typography>
                    )}
                    <AppButton variant="secondary" onClick={handleCloseAndClear}>Annuler</AppButton>
                    <AppButton variant="primary" onClick={handlePrint} disabled={!ready} startIcon={<PrintOutlined fontSize="small" />}>
                        Imprimer
                    </AppButton>
                </>
            )}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(340px, 5fr) 6fr' }, gap: 3, minHeight: { md: '62vh' } }}>
                {form}
                {preview}
            </Box>
        </Modal>
    );
};

export default GenerateModal;
